import { DOCUMENT } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, QueryList, SimpleChanges, ViewChild, ViewChildren, Inject, AfterContentChecked, AfterViewChecked, ChangeDetectorRef, NgZone } from '@angular/core';
import { CompactType, DisplayGrid, GridsterComponent, GridsterConfig, GridsterItemComponent, GridsterPush, GridType } from 'angular-gridster2';
import { Subscription } from 'rxjs';
import { NavigationService } from 'src/app/navigation/navigation.service';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { TimezoneService } from 'src/app/reusables/services/timezone.service';
import { DashboardManager } from 'src/shared/models/dashboard-manager';
import { Dashboard, DashboardSelectionListApiModel } from 'src/shared/models/dashboard.model';
import { DashboardModuleEagerLoad, DashboardModuleMap, DashboardModuleResponsive, DashboardModuleResponsiveInstance, DashboardModuleResponsiveInstanceForGridster, ModuleInfo } from 'src/shared/models/module-info.model';
import { DashboardService } from '../dashboard.service';
import { ModuleContainerComponent } from '../module-container/module-container.component';
import { ModuleContainerService } from '../module-container/module-container.service';

@Component({
    selector: 'cv-responsive-dashboard',
    templateUrl: './responsive-dashboard.component.html',
    styleUrls: ['./responsive-dashboard.component.scss']
})
export class ResponsiveDashboardComponent implements OnChanges, AfterViewInit, OnDestroy, AfterContentChecked {
    /** The unloaded dashboard model from the DashboardService's list. */
    @Input() dashboardInfo: Dashboard;
    @Input() reinitializeOnChanges = false;
    @Input() iscxonedashboard = false;
    @Input() activeDashboardKey: string = '';
    @Input() loadActiveDashboard: boolean = false;

    @Output() updateDashboard = new EventEmitter();

    @ViewChild('gridBackground', { read: ElementRef }) gridBackgroundEl: ElementRef<HTMLDivElement>;
    @ViewChild('gridCanvas', { read: GridsterComponent }) gridster: GridsterComponent;
    @ViewChild('gridCanvas', { read: ElementRef }) gridsterEl: ElementRef<HTMLElement>;
    @ViewChild('dashboardHeader', { read: ElementRef }) dashboardHeaderEl: ElementRef<HTMLElement>;
    @ViewChildren(ModuleContainerComponent) moduleContainerList: QueryList<ModuleContainerComponent>;

    /** The modules available to be placed on this dashboard. */
    availableModules: ModuleInfo[];
    /** The modules known to not be available to be placed on this dashboard. */
    blockedModules: DashboardModuleResponsive[];
    /** The loaded dashboard model. */
    dashboard: Dashboard;
    /** The selected dashboard list model.  */
    dashboardListItemModel: DashboardSelectionListApiModel;
    /** The key to identify this dashboard. */
    dashboardKey: string;
    /** A shared class for other components to interact with this dashboard. */
    dashboardManager: DashboardManager;
    /** The options to configure gridster. */
    gridsterOptions: GridsterConfig;
    /** The modules currently on this dashboard. */
    modules: DashboardModuleResponsiveInstanceForGridster[];
    /** Data that is already loaded for the modules on this dashboard. */
    eagerLoadModules: Record<string, DashboardModuleEagerLoad>;
    /** Whether this dashboard has focus. */
    hasFocus = false;
    /** Whether this dashboard has ever had focus. */
    hasBeenActivated = false;
    /** Whether this dashboard needs to be published. */
    publishPending = false;
    /** Whether this cxone dashboard needs to save changes. */
    editPending = false;
    /** Whether the publish banner is being moused over */
    publishMouseOver = false;
    /** Whether this dashboard is a temporary dashboard that has not been saved yet. */
    temporaryDashboardPending = false;
    /** The module currently being dragged. */
    placeholderModule: DashboardModuleResponsiveInstanceForGridster = null;
    /** Number of drags in process */
    dragEnterCount = 0;
    /** Currently focused module */
    focusedModuleKey: string = null;
    /** Whether this dashboard is in fullscreen mode */
    isFullscreen = false;
    /** Whether Add Widget is open */
    addingWidget = false;
    /** Whether this is a shared dashboard via url*/
    isSharedURL = false;
    /** Use to revert the temporary saved dashboard filters*/
    revertDashboardFilters = new EventEmitter<any>();

    /** Whether to show the loader for this dashboard. */
    get showLoader() {
        return !this.hasBeenActivated || this.states.dashboardModules.loading || this.states.publish.loading;
    }

    /** Whether the current dashboard is the active dashboard */
    get isActive() {
        return this.dashboard?.DashboardKey === this.service?.activeDashboard?.key;
    }

    /** Loading and success states pertaining to this dashboard. */
    states = {
        dashboardModules: {
            loading: false,
            success: false
        },
        publish: {
            loading: false,
            success: false
        }
    };

    private _subscriptions: Subscription[] = [];

    private _calculateLayout = true;

    constructor(
        public service: DashboardService,
        public moduleContainerService: ModuleContainerService,
        public navigationService: NavigationService,
        public timeZoneService: TimezoneService,
        private cvLocalizationService: CVLocalizationService,
        private cvConfigurationService: CvConfigurationService,
        @Inject(DOCUMENT) private document: Document,
        private _ngZone: NgZone,
        private _elementRef: ElementRef,
    ) {
        cvLocalizationService.GetSection("Responsive_Dashboard");
        this.dashboardManager = new DashboardManager(this, this.service);

        this.isSharedURL = window.location.pathname.includes('SharedDashboard.aspx');

        this.gridsterOptions = {
            gridType: GridType.ScrollVertical,
            minCols: this.service.GRIDSTER_COLUMNS,
            maxCols: this.service.GRIDSTER_COLUMNS,
            margin: 16,
            outerMargin: true,
            displayGrid: DisplayGrid.None,
            pushItems: true,
            pushDirections: {
                north: false,
                east: false,
                south: true,
                west: false,
            },
            minRows: 13,
            maxRows: 250,
            defaultItemCols: 2,
            defaultItemRows: 2,
            compactType: CompactType.CompactUp,
            disableScrollHorizontal: true,
            useTransformPositioning: false,
            swap: false,
            itemResizeCallback: (gridsterItem, component) => {
                const item = gridsterItem as DashboardModuleResponsiveInstanceForGridster;
                this.dashboardManager.moduleResizing.next({ item, component });
            },
            resizable: {
                enabled: this.isSharedURL ? false : this.dashboardManager.writeAccess || this.iscxonedashboard, // whether dragging items is supported
                handles: {
                    s: true,
                    e: true,
                    n: false,
                    w: true,
                    se: true,
                    ne: false,
                    sw: true,
                    nw: false,
                },
                start: (gridsterItem, component, event) => {
                    const item = gridsterItem as DashboardModuleResponsiveInstanceForGridster;
                    this.dashboardManager.moduleResizeStarted.next({ item, component, event });
                    this.moduleContainerService.closeAllMenusEvent.next();
                },
                stop: (gridsterItem, component, event) => {
                    const item = gridsterItem as DashboardModuleResponsiveInstanceForGridster;
                    this.dashboardManager.moduleResizeStopped.next({ item, component, event });
                },
            },
            draggable: {
                enabled: this.isSharedURL ? false : this.dashboardManager.writeAccess || this.iscxonedashboard,
                ignoreContent: true,
                dragHandleClass: 'gridsterDragHandle',
                ignoreContentClass: 'moduleHeaderIcon',
                start: (gridsterItem, component, event) => {
                    const item = gridsterItem as DashboardModuleResponsiveInstanceForGridster;
                    this.dashboardManager.moduleDragStarted.next({ item, component, event });
                    this.moduleContainerService.closeAllMenusEvent.next();
                },
                stop: (gridsterItem, component, event) => {
                    const item = gridsterItem as DashboardModuleResponsiveInstanceForGridster;
                    this.dashboardManager.moduleDragStopped.next({ item, component, event });
                },
            }
        };

        // Only resize CXone dashboards from the bottom corners
        if (cvConfigurationService.isCxOne) {
            this.gridsterOptions.resizable.handles = {
                s: false,
                e: false,
                n: false,
                w: false,
                se: true,
                ne: false,
                sw: true,
                nw: false,
            };
        }

        this._subscriptions.push(moduleContainerService.moduleGainedFocus.subscribe(key => this.focusedModuleKey = key));
        this._subscriptions.push(service.addModuleEvent.subscribe(module => this.onModuleClicked(module)));
        this._subscriptions.push(service.requestPublishNeeded.subscribe(key => {
            if (key == null || this.dashboard.DashboardKey !== key)
                return;
            this.dashboardManager.requestPublishNeeded();
        }));
        this._subscriptions.push(this.service.cancelPublish.subscribe(() => {
            if (this.publishPending)
                this.discardChanges();
            else 
                this.dashboardManager.reloadDashboard();
        }));
    }

    ngAfterContentChecked(): void {
        if (this._calculateLayout && !this._elementRef.nativeElement.parentElement.classList.contains('ng-hide')) {
            this.gridster?.calculateLayout();
            this._calculateLayout = false;
        }
    }

    @HostListener('document:fullscreenchange', ['$event'])
    @HostListener('document:webkitfullscreenchange', ['$event'])
    @HostListener('document:mozfullscreenchange', ['$event'])
    @HostListener('document:MSFullscreenChange', ['$event'])
    onFullscreenChanged() {
        if (this.isAnyBrowserFullscreen()) {
            this.isFullscreen = true;
        } else {
            this.isFullscreen = false;
        }
    }

    ngAfterViewInit(): void {

    }

    onInit(): void {
        this.dashboardKey = this.dashboardInfo.DashboardKey;
        this.dashboardInfo.states = this.states;
        this.service.dashboardManagers.set(this.dashboardKey, this.dashboardManager);
        if(this.iscxonedashboard){
            this.document.body.classList.add('cx-one-ui');
        }


        this._subscriptions.push(this.moduleContainerService.addModuleToDashboard.subscribe(moduleInstance => {
            if (this.dashboardKey !== moduleInstance.DashboardKey)
                return;

            this.dashboardManager.addCopiedModule(moduleInstance.Instance);
            this.dashboardManager.updateModuleMaps();
            this.dashboardManager.requestPublishNeeded();
        }));

        this._subscriptions.push(this.service.activeChanged.subscribe(keys => {
            const oldKey = keys[1];
            const newKey = keys[0];

            if (newKey === this.dashboardKey)
                this.onActivated();
            else if (oldKey === this.dashboardKey)
                this.onDeactivated();
        }));

        this._subscriptions.push(this.service.reloadDashboards.subscribe(keys => {
            if (keys[0].includes(this.dashboardKey)) {
                this.dashboardManager.reloadDashboard(keys[1]);
            }
        }));

        this._subscriptions.push(this.service.refreshTimeZoneInfo.subscribe(async key => {
            if (key === this.dashboardKey) {
                await this.refreshTimeZone();
            }
        }));
    }

    ngOnChanges(changes: SimpleChanges) {
        const currentKey = changes?.dashboardInfo?.currentValue?.DashboardKey || this.dashboardInfo.DashboardKey;
        const shouldLoadDashboard = this.reinitializeOnChanges || !this.reinitializeOnChanges && changes.dashboardInfo?.isFirstChange();
        const loadActiveDashboard = this.loadActiveDashboard || changes.loadActiveDashboard?.currentValue;
        if (currentKey !== this.activeDashboardKey && (loadActiveDashboard || !shouldLoadDashboard)) return;

        if (this.dashboardInfo && (!this.service.isDashboardAlreadyLoaded(currentKey) || shouldLoadDashboard)) {
            this.onInit();
            this.initDashboard();
        }
        this._ngZone.run(() => {
            this.navigationService.closeModuleMenuSubject.subscribe(() => this.closeModuleMenuEvent());
        });
    }

    closeModuleMenuEvent() {
        this._ngZone.run(() => {
            this.gridster?.calculateLayout();
        });
    }

    ngOnDestroy() {
        this._subscriptions?.forEach(sub => sub?.unsubscribe());
        this.navigationService.closeModuleMenuSubject.unsubscribe();
    }

    /** Loads the dashboard data from the backend */
    async initDashboard() {
        this.states.dashboardModules.loading = true;

        try {
            const loadedDashboard = await this.service.loadDashboard(this.dashboardKey);
            if (this.service.isDashboardProjection && loadedDashboard.dashboardKey !== this.service.activeDashboard.key)
                return;
            this.dashboard = loadedDashboard.dashboard;
            this.dashboard.writeAccess = loadedDashboard.SharedWriteAccess;
            this.dashboard.readGroupAccess = loadedDashboard.SharedReadGroupAccess;
            this.dashboard.readControlAccess = loadedDashboard.SharedReadControlAccess;
            this.dashboard.readAccess = loadedDashboard.SharedReadAccess;
            this.dashboard.sharedOwner = loadedDashboard.SharedOwner;
            this.dashboard.isShared = loadedDashboard.dashboard.RequestingLoginID > 0;
            this.temporaryDashboardPending = this.dashboard.TemporaryDashboard;
            this.availableModules = this.parseAvailableModulesFromApi(loadedDashboard.availableModules);
            this.blockedModules = loadedDashboard.blockedModules;
            this.eagerLoadModules = loadedDashboard.modulesEagerLoad;

            // Update the navigation factory's module list if this is the focused dashboard.
            if (this.hasFocus) {
                this.navigationService.setModules(this.availableModules);
                this.navigationService.activeDashboard = this.dashboard;
            }

            if (this.service.isDashboardWindow) {
                this.navigationService.activeDashboard = this.dashboard;
                this.hasBeenActivated = true;
            }

            if (loadedDashboard.modules != null && !this.publishPending) {
                this.modules = loadedDashboard.modules.map(m =>
                    DashboardModuleResponsiveInstanceForGridster.fromDashboardModuleResponsiveInstance(m));
                this.dashboardManager.setInitialMaps(this.getModuleMaps());
                // check here
            }

            if (!this.iscxonedashboard && (!this.dashboardManager.writeAccess || this.isSharedURL)) {
                this.gridsterOptions.resizable.enabled = false;
                this.gridsterOptions.draggable.enabled = false;
                this.gridster.optionsChanged();
            }

            this.states.dashboardModules.success = true;
        } catch (error) {
            this.states.dashboardModules.success = false;
            this.service.removeDashboard(null, this.dashboardKey, false);
            if (!this.iscxonedashboard) { // TODO: showErrorDialog is obsolete and needs to be changed
                const errorMsg = this.cvLocalizationService.getCachedText("Responsive_Dashboard", "Error_Message", "Error opening dashboard");
                window['showErrorDialog'](error.data.message + " - " + error.data.warnings[0], errorMsg);
            }
                
        } finally {
            if (!this.service.isDashboardProjection || this.isActive) {
                if (this.service.activeDashboard.key === this.dashboardKey) {
                    this.onActivated();
                } else {
                    this.onDeactivated();
                }
                this.states.dashboardModules.loading = false;
            }
        }
    }

    /** Loads the dashboard's time zone and refreshes each widget to display data and the last refresh time for the right time zone offset */
    async refreshTimeZone() {
        this.states.dashboardModules.loading = true;

        try {
            const timezone = await this.timeZoneService.getDashboardTimezone(this.dashboard.DashboardKey);
            this.dashboard.TimeZoneInfoName = timezone.TimezoneName;

            this.moduleContainerList.forEach(container => {
                container.refreshClicked();
            });

            this.states.dashboardModules.success = true;
        } finally {
            this.states.dashboardModules.loading = false;
        }
    }

    // set ModuleID number as case and give it a return value. this field is sorted on numerically ascending order in navigation-service-base.ts ~line 248
    getPosition(moduleID: number) {
        switch (moduleID) {
            case 19:
                return 1;
            case 21:
                return 2;
            case 110:
                return 3;
            case 124:
                return 4;
            case 59:
                return 5;
            case 100:
                return 6;
            case 101:
                return 7;
            case 108:
                return 8;
            case 2:
                return 9;
            case 112:
                return 10;
            case 111:
                return 11;
            case 131:
                return 12;
            default: // cxone wont have other widgets but if they do put them at the end of the widgets top bar until we know where they want them
                return 10000;

        }
    }

    /** Converts each DashboardModuleResponsive into a usable ModuleInfo. */
    parseAvailableModulesFromApi(availableModulesApiModel: DashboardModuleResponsive[]) {
        const moduleTypes: ModuleInfo[] = [];
        for (const moduleApiModel of availableModulesApiModel) {
            /* eslint-disable @typescript-eslint/naming-convention */ // because names come from DTO
            const newModuleType: ModuleInfo = {
                DTO: moduleApiModel,
                ModuleID: moduleApiModel.ModuleID,
                ModuleName: moduleApiModel.ModuleName,
                ModuleDescription: moduleApiModel.ModuleDescription,
                ModuleGroup: moduleApiModel.ModuleGroup,
                Resizable: moduleApiModel.Resizable,
                LocalizedName: moduleApiModel.LocalizedName,
                LocalizedGroup: moduleApiModel.LocalizedGroup,
                PreviewImagePath: moduleApiModel.PreviewImagePath,
                RelatedID: moduleApiModel.RelatedID,
                cxonePosition: this.getPosition(moduleApiModel.ModuleID),
                map: {
                    size: [moduleApiModel.DefaultSizeX, moduleApiModel.DefaultSizeY],
                    minSize: [moduleApiModel.MinSizeX, moduleApiModel.MinSizeY],
                    maxSize: [moduleApiModel.MaxSizeX, moduleApiModel.MaxSizeY],
                    position: [null, null]
                },
                options: {
                    tableAvailable: moduleApiModel.TableAvailable,
                    chartAvailable: moduleApiModel.ChartAvailable,
                    tableAndChartAvailable: moduleApiModel.TableAndChartAvailable,
                    mobileViewAvailable: moduleApiModel.MobileViewAvailable,
                    fullScreenViewAvailable: moduleApiModel.FullScreenViewAvailable,
                    settingsAvailable: moduleApiModel.SettingsAvailable,
                    canExportAndSubscribe: moduleApiModel.CanExportAndSubscribe,
                    hasGenericSettings: moduleApiModel.SettingEmpSelector
                        || moduleApiModel.SettingRecSelector
                        || moduleApiModel.SettingAttSelector
                        || moduleApiModel.SettingMetricSelector
                        || moduleApiModel.SettingDateRangeSelector
                        || moduleApiModel.SettingTimeRangeSelector
                        || moduleApiModel.SettingIntervalSelector
                },
                ModuleSource: moduleApiModel.ModuleSource
            };

            // Check that directive exists
            if (this.service.directiveExists(newModuleType.ModuleSource))
                moduleTypes.push(newModuleType);
        }
        return moduleTypes;
    }

    /** Marks the dashboard as having focus. */
    onActivated() {
        this._calculateLayout = true;
        this.hasBeenActivated = true;
        this.hasFocus = true;
        this.navigationService.setModules(this.availableModules);
        this.navigationService.activeDashboard = this.dashboard;
    }

    /** Marks the dashboard as no longer having focus. */
    onDeactivated() {
        this.hasFocus = false;
    }

    dragEnter(event: DragEvent) {
        if (!this.dashboardManager.writeAccess || this.isSharedURL)
            return;

        this.dragEnterCount++;
        if (this.dragEnterCount > 1)
            return;

        const module = this.service.draggingModule;

        if (module?.ModuleID) {
            this.addModule(module, -1, -1, module.moduleKey, true);
        }
    }

    dragOver(event: DragEvent) {
        if (!this.dashboardManager.writeAccess || this.isSharedURL)
            return;

        if (this.navigationService.addingModule || this.navigationService.showOverlay)
            return;

        if (this.placeholderModule) {
            const gridsterItem = this.gridster.findItemWithItem(this.placeholderModule);
            if (gridsterItem) {
                const gridsterItemComponent = gridsterItem as GridsterItemComponent;
                const boundingRect = this.gridsterEl.nativeElement.getBoundingClientRect();
                const pixelX = event.pageX - boundingRect.left - (this.gridsterOptions.margin * 2);
                const pixelY = event.pageY - boundingRect.top - (this.gridsterOptions.margin * 2) + this.gridsterEl.nativeElement.scrollTop;
                const x = this.gridster.pixelsToPositionX(pixelX, Math.floor);
                const y = this.gridster.pixelsToPositionY(pixelY, Math.floor);

                if (x !== gridsterItemComponent.$item.x || y !== gridsterItemComponent.$item.y) {
                    const push = new GridsterPush(gridsterItemComponent); // init the service
                    gridsterItemComponent.$item.x = x;
                    gridsterItemComponent.$item.y = y;
                    push.pushItems(push.fromNorth);
                    push.checkPushBack(); // check for items can restore to original position
                    push.setPushedItems(); // save the items pushed
                    gridsterItemComponent.checkItemChanges(gridsterItemComponent.$item, gridsterItemComponent.item);
                    push.destroy();
                }
            }
        }
        event.preventDefault();
    }

    dragLeave(event: DragEvent) {
        if (!this.dashboardManager.writeAccess || this.isSharedURL)
            return;

        this.dragEnterCount--;
        if (this.dragEnterCount > 0)
            return;

        for (let i = 0; i < this.modules.length; i++) {
            const module = this.modules[i];
            if (module.isPlaceholder) {
                this.modules.splice(i, 1);
                i--;
            }
        }

        this.placeholderModule = null;
    }

    addModule(module: ModuleInfo, row = -1, col = -1, moduleKey: string = undefined, placeholder = false) {
        const moduleToAdd: DashboardModuleResponsiveInstance = JSON.parse(JSON.stringify(module.DTO));
        moduleToAdd.map = {
            size: [moduleToAdd?.MinSizeX && moduleToAdd?.DefaultSizeX < moduleToAdd?.MinSizeX ? moduleToAdd?.MinSizeX : moduleToAdd?.DefaultSizeX,
                moduleToAdd?.MinSizeY && moduleToAdd?.DefaultSizeY < moduleToAdd?.MinSizeY ? moduleToAdd?.MinSizeY : moduleToAdd?.DefaultSizeY],
            position: [null, null],
            minSize: [moduleToAdd.MinSizeX, moduleToAdd.MinSizeY],
            maxSize: [moduleToAdd.MaxSizeX, moduleToAdd.MaxSizeY],
        };
        moduleToAdd.hasAccess = true;
        moduleToAdd.RelatedID = module.RelatedID;
        moduleToAdd.fromTaskbar = module.fromTaskbar;

        if (moduleKey == null) {
            moduleToAdd.moduleKey = null;
        }

        if (row > -1 && col > -1) {
            moduleToAdd.map.position[0] = row;
            moduleToAdd.map.position[1] = col;
        }

        const gridsterModule = DashboardModuleResponsiveInstanceForGridster.fromDashboardModuleResponsiveInstance(moduleToAdd);
        if (placeholder) {
            gridsterModule.isPlaceholder = true;
            this.placeholderModule = gridsterModule;
        }
        // check here
        this.modules.push(gridsterModule);
        this.modules = this.modules.slice();
    }

    onDrop(event: DragEvent) {
        this.dragEnterCount = 0;

        if (!this.dashboardManager.writeAccess || this.isSharedURL)
            return;

        if (this.navigationService.addingModule || this.navigationService.showOverlay)
            return;

        for (const module of this.modules) {
            if (module.isPlaceholder)
                module.isPlaceholder = false;
        }
        this.placeholderModule = null;
    }

    onModuleClicked(module: ModuleInfo) {
        if (this.hasFocus) {
            this.addModule(module, -1, -1, module.moduleKey);
        }
    }

    async publishChanges() {
        this.states.publish.loading = true;
        try {
            const response = await this.service.publishChanges(this.dashboardKey, this.iscxonedashboard);
            this.publishPending = false;
            this.editPending = false;
            this.dashboardManager.resetModuleMapUpdatePrevention();
            this.dashboardInfo.NeedConfirmationToClose = false;
            this.dashboardInfo.publishPending = false;
            this.states.publish.success = true;
            this.eagerLoadModules = response.modulesEagerLoad;
            this.service.synchronizeDashboard(response);
            this.closeAddWidget();
        } catch {
            this.states.publish.success = false;
        } finally {
            this.states.publish.loading = false;
            if (this.cvConfigurationService.isCxOne)
                sendMessageCXOne({ hasNonSavedChanges: false });
        }
    }

    discardChanges() {
        this.publishPending = false;
        this.editPending = false;
        this.dashboardManager.resetModuleMapUpdatePrevention();
        this.dashboardInfo.NeedConfirmationToClose = false;
        this.dashboardInfo.publishPending = false;
        this.closeAddWidget();
        this.revertDashboardFilters.emit();
        this.dashboardManager.reloadDashboard();
        if (this.cvConfigurationService.isCxOne) {
            this.exitFullscreenMode();
            sendMessageCXOne({ hasNonSavedChanges: false });
        }
    }

    async saveCXOneTemplateChanges() {
        this.states.publish.loading = true;
        try {
            const response = await this.service.updateSystemTemplateFromDashboard(this.dashboardKey);
            this.publishPending = false;
            this.editPending = false;
            this.dashboardManager.resetModuleMapUpdatePrevention();
            this.dashboardInfo.NeedConfirmationToClose = false;
            this.dashboardInfo.publishPending = false;
            this.states.publish.success = true;
            this.eagerLoadModules = response.modulesEagerLoad;
            this.service.synchronizeDashboard(response);
            this.closeAddWidget();
        } catch {
            this.states.publish.success = false;
        } finally {
            this.states.publish.loading = false;
            if (this.cvConfigurationService.isCxOne)
                sendMessageCXOne({ hasNonSavedChanges: false });
        }
    }

    exitFullscreenMode() {
        this.moduleContainerList.forEach(mc => {
            if (mc.isFullscreen)
                mc.recoverFromFullscreen();
        });
    }

    async saveTemporaryDashboard() {
        const data = {
            dashboardName: this.dashboard.DashboardName ?? '',
            description: this.dashboard.DashboardDescription ?? '',
            folder: this.dashboard.DashboardGroup ?? '',
            key: this.dashboard.DashboardKey,
        };
        this.service.openSaveDashboardModal(data, 'SaveTemplate').subscribe(dashboard => {
            if (dashboard) {
                this.temporaryDashboardPending = false;
                // Updates the name and current info of the current workspace tab:
                this.dashboardInfo.DashboardName = dashboard.DashboardName;
                this.dashboardInfo.DashboardGroup = dashboard.DashboardGroup;
                this.dashboardInfo.DashboardDescription = dashboard.DashboardDescription;

                this.updateDashboard.emit();
            }
        });
    }

    discardTemporaryDashboard() {
        this.service.discardTemporaryDashboardEvent.next(this.dashboardKey);
    }

    toggleFullscreen() {
        if (!this.isAnyBrowserFullscreen()) {
            if (document.documentElement.requestFullscreen) {
                document.documentElement.requestFullscreen();
            } else if (document.documentElement['mozRequestFullScreen']) {
                document.documentElement['mozRequestFullScreen'](); // Firefox
            } else if (document.documentElement['webkitRequestFullscreen']) {
                document.documentElement['webkitRequestFullscreen'](); // Chrome and Safari
            } else if (document.documentElement['msRequestFullscreen']) {
                document.documentElement['msRequestFullscreen'](); // IE
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document['msExitFullscreen']) {
                document['msExitFullscreen'](); // IE
            } else if (document['mozCancelFullScreen']) {
                document['mozCancelFullScreen'](); // Firefox
            } else if (document['webkitExitFullscreen']) {
                document['webkitExitFullscreen'](); // Chrome and Safari
            }
        }
    }

    /** Retrieves a list of the modules' keys and positions. */
    getModuleMaps(): DashboardModuleMap[] {
        return this.modules.map(module => ({
            moduleKey: module.moduleKey,
            position: [module.y, module.x],
            size: [module.cols, module.rows],
        }));
    }

    private isAnyBrowserFullscreen() {
        return document.fullscreenElement ||
            document['mozFullScreenElement'] ||
            document['webkitFullscreenElement'] ||
            document['msFullscreenElement'];
    }

    public toggleAddingWidget() {
        this.addingWidget = !this.addingWidget;
    }

    public closeAddWidget() {
        this.addingWidget = false;
    }
}
